import { PortalUser, PortalUserRole } from '../model/PortalUserList.types';

export type PortalUserStatusFilter = 'all' | 'active' | 'disabled';
export type PortalUserLinkedFilter = 'all' | 'linked' | 'unlinked';
export type PortalUserSortField = 'id' | 'name';

export type PortalUserFilters = {
  search: string;
  role: PortalUserRole | null;
  status: PortalUserStatusFilter;
  linked: PortalUserLinkedFilter;
};

const matchesStatus = (
  user: PortalUser,
  status: PortalUserStatusFilter,
): boolean => {
  if (status === 'all') return true;
  return status === 'active' ? user.isActive : !user.isActive;
};

const matchesRole = (
  user: PortalUser,
  role: PortalUserRole | null,
): boolean => {
  if (role === null) return true;
  return user.role === role;
};

const matchesLinked = (
  user: PortalUser,
  linked: PortalUserLinkedFilter,
): boolean => {
  if (linked === 'all') return true;
  return linked === 'linked'
    ? user.influencerId !== null
    : user.influencerId === null;
};

const matchesSearch = (user: PortalUser, search: string): boolean => {
  const trimmed = search.trim().toLowerCase();
  if (!trimmed) return true;
  const haystack =
    `${user.firstName} ${user.lastName} ${user.email} ${user.id}`.toLowerCase();
  return haystack.includes(trimmed);
};

export const filterPortalUsers = (
  users: PortalUser[],
  filters: PortalUserFilters,
): PortalUser[] =>
  users.filter(
    (user) =>
      matchesStatus(user, filters.status) &&
      matchesRole(user, filters.role) &&
      matchesLinked(user, filters.linked) &&
      matchesSearch(user, filters.search),
  );

export const sortPortalUsers = (
  users: PortalUser[],
  by: PortalUserSortField,
): PortalUser[] => {
  const sorted = [...users];

  if (by === 'id') {
    sorted.sort((left, right) => right.id - left.id);
    return sorted;
  }

  sorted.sort((left, right) => {
    const firstNameCompare = left.firstName.localeCompare(
      right.firstName,
      undefined,
      {
        sensitivity: 'base',
      },
    );
    if (firstNameCompare !== 0) return firstNameCompare;
    return left.lastName.localeCompare(right.lastName, undefined, {
      sensitivity: 'base',
    });
  });

  return sorted;
};
